/**
 * API Module - Centralized API exports.
 *
 * Usage:
 *
 * ```typescript
 * import { api } from '@/lib/api';
 *
 * // Matters
 * const matters = await api.matters.list();
 * const matter = await api.matters.create({ name: 'New Matter' });
 *
 * // Documents
 * const docs = await api.documents.list(matterId);
 * const results = await api.documents.search({ query: 'contract', matter_id: matterId });
 *
 * // Preflight (FREE analysis)
 * const preflight = await api.preflight.uploadAndAnalyze(matterId, files);
 * const result = await api.preflight.waitForCompletion(preflight.preflight_id);
 *
 * // Investigations
 * const investigation = await api.investigations.create({ matter_id: matterId, claims: [...] });
 * await api.investigations.start(investigation.id);
 *
 * // Auth
 * await api.auth.login({ email, password });
 * const user = api.auth.getCachedUser();
 * ```
 */

// Core client and types
export { apiClient, type RequestConfig, type ApiResponse } from './client';
export { ApiError, isApiError } from './types';
export type * from './types';

// API modules
export { authApi } from './auth';
export { mattersApi } from './matters';
export { auditApi } from './audit';
export { documentsApi } from './documents';
export { asksApi } from './asks';
export type { Ask, AskEvidence, ImportResult, SearchResult } from './asks';
export { preflightApi } from './preflight';
export { investigationsApi } from './investigations';
export { processingApi } from './processing';
export { matterActivityApi } from './matter-activity';
export type * from './matter-activity';
export { exportsApi } from './exports';
export { handoffApi } from './handoff';
export { feedbackApi, feedbackAdminApi } from './feedback';
export { waitlistApi } from './waitlist';
export { reviewApi } from './review';
export type * from './review-types';
export { reviewPredictiveApi } from './review-predictive';
export type * from './review-predictive-types';
export { redactionsApi } from './redactions';
export type * from './redactions-types';
export { citationsApi } from './citations';
export type * from './citations';
export { complianceApi } from './compliance';
export type * from './compliance-types';
export { mediaApi } from './media';
export type * from './media';
export { matterRulesApi } from './matter-rules';
export type { MatterRule, CreateMatterRuleRequest } from './matter-rules';
export { assistantApi } from './assistant';
export { approvalsApi } from './approvals';
export type * from './approvals';
export { sweepApi, aiSpendApi } from './sweep';
export { coverageApi } from './coverage';
export type * from './coverage';
export { costsApi } from './costs';
export type * from './costs';
export { productionApi } from './production';
export type * from './production';
export { hotDocPropagationApi } from './hot-doc-propagation';
export type * from './hot-doc-propagation';
export { codingBucketsApi } from './coding-buckets';
export type * from './coding-buckets';
export { namedCollectionsApi } from './named-collections';
export type * from './named-collections';
// Types from sweep.ts - explicitly listed to avoid conflicts with Citation and ReviewDecision
export type {
  SweepRequestStatus,
  SweepRunStatus,
  FidelityTier,
  IssueType,
  Severity,
  PolicyDecision,
  SpendRequestStatus,
  QuoteStatus,
  FullMatterTarget,
  SearchIdTarget,
  DocumentIdsTarget,
  TargetSpec,
  SweepRequestCreate,
  SweepRequestUpdate,
  SweepRequestResponse,
  SweepRequestListResponse,
  SweepRunResponse,
  SweepRunListResponse,
  SweepRunDocumentResponse,
  SweepFlagResponse,
  SweepFlagListResponse,
  SweepFlagReview,
  SweepFlagBulkReview,
  SweepReportResponse,
  IssueSummary,
  SweepQuoteRequest,
  SweepQuoteResponse,
  CreateReviewQueuesRequest,
  CreateReviewQueuesResponse,
  CatalogModelInfo,
  CatalogSnapshotResponse,
  QuoteRequest,
  QuoteResponse,
  SpendRequestCreate,
  SpendRequestResponse,
  SpendRequestDecision,
  SpendSummary,
  SweepFlagQuery,
  SweepRequestQuery,
} from './sweep';

// Convenience namespace
import { authApi } from './auth';
import { mattersApi } from './matters';
import { auditApi } from './audit';
import { documentsApi } from './documents';
import { asksApi } from './asks';
import { preflightApi } from './preflight';
import { investigationsApi } from './investigations';
import { processingApi } from './processing';
import { matterActivityApi } from './matter-activity';
import { exportsApi } from './exports';
import { handoffApi } from './handoff';
import { feedbackApi, feedbackAdminApi } from './feedback';
import { waitlistApi } from './waitlist';
import { reportPackApi } from './report-pack';
import { reviewApi } from './review';
import { reviewPredictiveApi } from './review-predictive';
import { redactionsApi } from './redactions';
import { citationsApi } from './citations';
import { complianceApi } from './compliance';
import { mediaApi } from './media';
import { matterRulesApi } from './matter-rules';
import { assistantApi } from './assistant';
import { approvalsApi } from './approvals';
import { sweepApi, aiSpendApi } from './sweep';
import { coverageApi } from './coverage';
import { costsApi } from './costs';
import { productionApi } from './production';
import { hotDocPropagationApi } from './hot-doc-propagation';
import { codingBucketsApi } from './coding-buckets';
import { namedCollectionsApi } from './named-collections';

export const api = {
  auth: authApi,
  audit: auditApi,
  matters: mattersApi,
  documents: documentsApi,
  asks: asksApi,
  preflight: preflightApi,
  investigations: investigationsApi,
  processing: processingApi,
  matterActivity: matterActivityApi,
  exports: exportsApi,
  handoff: handoffApi,
  feedback: feedbackApi,
  feedbackAdmin: feedbackAdminApi,
  waitlist: waitlistApi,
  reportPack: reportPackApi,
  review: reviewApi,
  reviewPredictive: reviewPredictiveApi,
  redactions: redactionsApi,
  citations: citationsApi,
  compliance: complianceApi,
  media: mediaApi,
  matterRules: matterRulesApi,
  assistant: assistantApi,
  approvals: approvalsApi,
  sweep: sweepApi,
  aiSpend: aiSpendApi,
  coverage: coverageApi,
  costs: costsApi,
  production: productionApi,
  hotDocPropagation: hotDocPropagationApi,
  codingBuckets: codingBucketsApi,
  namedCollections: namedCollectionsApi,
} as const;

export default api;
